/**
 * Money, as the breakdown cards render it.
 *
 * Two rules, both of which were being re-implemented per card:
 *
 * - the number is abbreviated (`1.24M`, `12.50K`) because these are comparison
 *   figures inside a bar row, not a ledger;
 * - the currency code comes from the endpoint, never assumed. A bare `$` would
 *   state a currency the payload never claimed, and a HKD book would read as USD
 *   to anyone who did not already know.
 *
 * `currency` is optional so a payload that genuinely carries none still renders:
 * the number is shown alone rather than labelled with a guess.
 */

/** The number alone: `1500` -> `1.50K`. */
export function formatAmountValue(value: number): string {
  // Thresholds on the magnitude, sign kept on the figure: `-1500000` is as much a
  // million as `1500000` is, and abbreviating only positive values would put a
  // seven-figure number next to abbreviated ones in the same column.
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (magnitude >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
  return value.toFixed(2);
}

/** The number with its currency code: `1500`, `'USD'` -> `USD 1.50K`. */
export function formatAmount(value: number, currency?: string | null): string {
  const formatted = formatAmountValue(value);
  return currency ? `${currency} ${formatted}` : formatted;
}
